"use client";

import { useState, useCallback } from "react";
import { useFacebookLogin } from "@/hooks/use-facebook-login";
import { useSocialLogin } from "@/hooks/use-auth";

/**
 * Google Identity Services types (loaded by GoogleOAuthProvider script).
 */
declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(config: {
            client_id: string;
            callback: (response: { credential: string; select_by: string }) => void;
            auto_select?: boolean;
            cancel_on_tap_outside?: boolean;
          }): void;
          prompt(momentListener?: (notification: {
            isNotDisplayed(): boolean;
            isSkippedMoment(): boolean;
            isDismissedMoment(): boolean;
          }) => void): void;
          renderButton(parent: HTMLElement, options: Record<string, unknown>): void;
          disableAutoSelect(): void;
        };
      };
    };
  }
}

/**
 * Social auth for page-level login/register (redirects to /home on success).
 *
 * Google sign-in is handled by the rendered GIS button via `onGoogleCredential`
 * on AuthMethodPicker. This hook provides `handleGoogleToken` to submit the
 * credential, plus Facebook login.
 */
export function useSocialAuth() {
  const socialLogin = useSocialLogin();
  const facebook = useFacebookLogin();
  const [activeProvider, setActiveProvider] = useState<
    "google" | "facebook" | null
  >(null);

  const handleGoogleToken = useCallback(
    (credential: string) => {
      setActiveProvider("google");
      socialLogin.mutate(
        { provider: "google_auth", token: credential },
        { onSettled: () => setActiveProvider(null) },
      );
    },
    [socialLogin],
  );

  const loginWithFacebook = useCallback(async () => {
    setActiveProvider("facebook");
    try {
      const token = await facebook.login();
      socialLogin.mutate(
        { provider: "facebook_auth", token },
        { onSettled: () => setActiveProvider(null) },
      );
    } catch {
      setActiveProvider(null);
    }
  }, [facebook, socialLogin]);

  return {
    handleGoogleToken,
    loginWithFacebook,
    isGoogleLoading: activeProvider === "google",
    isFacebookLoading: activeProvider === "facebook",
    isFacebookReady: facebook.isReady,
    error: socialLogin.error,
  };
}
